import { Extension, Command } from '@tiptap/core';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        textCase: {
            uppercaseSelection: () => ReturnType;
        };
    }
}

/**
 * Converts all text in the current selection to uppercase, preserving marks (bold, italic, etc.)
 * and the document structure (paragraphs, headings, etc.).
 *
 * How it works:
 * 1. Iterates through all text nodes within the selection range
 * 2. For each text node, calculates which portion falls within the selection
 * 3. Transforms that portion to uppercase
 * 4. Collects all needed replacements
 * 5. Applies them in reverse order (to avoid position shifts affecting subsequent replacements)
 *
 */
export const createUppercaseSelectionCommand =
    (): Command =>
    ({ state, dispatch }) => {
        const { from, to, empty } = state.selection;

        if (empty) return false;

        const replacements: { from: number; to: number; text: string }[] = [];

        state.doc.nodesBetween(from, to, (node, pos) => {
            if (!node.isText || !node.text) return;

            const start = Math.max(from, pos);
            const end = Math.min(to, pos + node.text.length);

            if (start >= end) return;

            const original = node.text.slice(start - pos, end - pos);
            const upper = original.toUpperCase();

            if (original !== upper) {
                replacements.push({ from: start, to: end, text: upper });
            }
        });

        if (!replacements.length || !dispatch) return true;

        // Apply replacements in reverse order so earlier edits do not shift the
        // positions of later ones. This matters because uppercasing can change
        // text length, e.g. "straße" -> "STRASSE".
        const tr = replacements.reverse().reduce((tr, { from, to, text }) => tr.insertText(text, from, to), state.tr);

        dispatch(tr);
        return true;
    };

export const TextCase = Extension.create({
    name: 'textCase',

    addCommands() {
        return {
            uppercaseSelection: () => createUppercaseSelectionCommand(),
        };
    },

    addKeyboardShortcuts() {
        return {
            /* eslint-disable @typescript-eslint/naming-convention */
            'Shift-F3': () => this.editor.commands.uppercaseSelection(),
            /* eslint-enable @typescript-eslint/naming-convention */
        };
    },
});
